/**
 * Institution Features Component
 * Display features for institutional landing pages
 */

import React from 'react';
import { Grid, Column, Tile } from '@carbon/react';
import { Education, Analytics, Security, Collaborate, CloudDataOps, UserMultiple } from '@carbon/icons-react';
import type { Feature } from '../../../../shared/interfaces/InstitutionalLanding';

interface InstitutionFeaturesProps {
  features: Feature[];
}

const InstitutionFeatures: React.FC<InstitutionFeaturesProps> = ({ features }) => {
  const getFeatureIcon = (index: number) => {
    const icons = [Education, Analytics, Security, Collaborate, CloudDataOps, UserMultiple];
    const Icon = icons[index % icons.length];
    return <Icon size={32} />;
  };

  return (
    <section className="institution-features-section">
      <Grid narrow>
        <Column lg={16}>
          <h2 className="features-title">Comprehensive Educational Platform</h2>
          <p className="features-subtitle">
            Everything your institution needs to deliver personalized, AI-powered education
          </p>
        </Column>

        {features.map((feature, index) => (
          <Column key={feature.id} lg={8} md={4} sm={4}>
            <Tile className="feature-card">
              <div className="feature-icon">{feature.icon || getFeatureIcon(index)}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
              {feature.benefits && feature.benefits.length > 0 && (
                <ul className="feature-benefits">
                  {feature.benefits.slice(0, 3).map((benefit, i) => (
                    <li key={i}>{benefit}</li>
                  ))}
                </ul>
              )}
              {feature.targetRole && <span className="feature-role">For {feature.targetRole.replace(/_/g, ' ')}</span>}
            </Tile>
          </Column>
        ))}
      </Grid>

      <style>{`
        .institution-features-section {
          padding: var(--cds-spacing-11) 0;
          background-color: var(--cds-background);
        }

        .features-title {
          font-size: var(--cds-display-01-font-size);
          font-weight: var(--cds-display-01-font-weight);
          margin-bottom: var(--cds-spacing-05);
          color: var(--cds-text-primary);
          text-align: center;
        }

        .features-subtitle {
          font-size: var(--cds-body-long-02-font-size);
          color: var(--cds-text-secondary);
          text-align: center;
          margin-bottom: var(--cds-spacing-10);
          max-width: 44rem;
          margin-left: auto;
          margin-right: auto;
        }

        .feature-card {
          height: 100%;
          padding: var(--cds-spacing-08);
          margin-bottom: var(--cds-spacing-08);
          transition: transform var(--cds-duration-fast-02) var(--cds-ease-out), box-shadow var(--cds-duration-fast-02) var(--cds-ease-out);
          background-color: var(--cds-layer-01);
          border: 1px solid var(--cds-border-subtle);
        }

        .feature-card:hover {
          transform: translateY(calc(-1 * var(--cds-spacing-02)));
          box-shadow: var(--cds-shadow);
          border-color: var(--cds-border-interactive);
        }

        .feature-icon {
          color: var(--cds-interactive-01, #8a3ffc);
          margin-bottom: var(--cds-spacing-05);
        }

        .feature-title {
          font-size: var(--cds-heading-03-font-size);
          font-weight: var(--cds-heading-03-font-weight);
          margin-bottom: var(--cds-spacing-04);
          color: var(--cds-text-primary);
        }

        .feature-description {
          font-size: var(--cds-body-short-01-font-size);
          color: var(--cds-text-secondary);
          line-height: var(--cds-body-short-01-line-height);
          margin-bottom: var(--cds-spacing-05);
        }

        .feature-benefits {
          list-style: none;
          padding: 0;
          margin: var(--cds-spacing-05) 0;
        }

        .feature-benefits li {
          font-size: var(--cds-helper-text-01-font-size);
          color: var(--cds-text-secondary);
          padding-left: var(--cds-spacing-06);
          position: relative;
          margin-bottom: var(--cds-spacing-03);
        }

        .feature-benefits li::before {
          content: '✓';
          position: absolute;
          left: 0;
          color: var(--cds-support-success);
          font-weight: bold;
        }

        .feature-role {
          display: inline-block;
          font-size: var(--cds-label-01-font-size);
          color: var(--cds-text-on-color);
          background-color: var(--cds-interactive-01);
          padding: var(--cds-spacing-02) var(--cds-spacing-03);
          margin-top: var(--cds-spacing-03);
          text-transform: capitalize;
          border-radius: var(--cds-border-radius);
        }
      `}</style>
    </section>
  );
};

export default InstitutionFeatures;
